import dayjs, { ManipulateType, OpUnitType } from "dayjs";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Legend,
  LabelList,
} from "recharts";
import { TooltipProps } from "recharts";
import { Card } from "~/components/Card";
import { Flex } from "~/components/Flex";
import { Text } from "~/components/Text";
import { asCurrency } from "~/utils/mask.utils";
import { MultiValue } from "../../DashboardCustomerPage";
import {
  FilterKeyType,
  parseChartEndDate,
  parseChartStartDate,
  parseFutureDate,
} from "../../utils";

const ClickableXAxisTick = ({
  x,
  y,
  payload,
  onSelectPeriodDate,
  filterKey,
}: {
  x: number;
  y: number;
  payload: any;
  onSelectPeriodDate: (value: MultiValue["filterRangePeriod"]) => void;
  filterKey: string;
}) => {
  const handleClick = () => {
    const date = dayjs(payload.value, "MMM").toDate();
    const range = {
      startDate: parseChartStartDate({ dateType: "month", date }),
      endDate: parseChartEndDate({ dateType: "month", date }),
      isPeriodOnBar: true,
    };
    onSelectPeriodDate(null);
    onSelectPeriodDate(range);
  };

  return (
    <text
      x={x}
      y={y + 10}
      textAnchor="middle"
      fill="#2563EB"
      style={{ cursor: "pointer", fontSize: 12, fontWeight: 500 }}
      onClick={handleClick}
    >
      {payload.value}
    </text>
  );
};

export const CustomTooltip = ({ active, payload, label }: TooltipProps<number, string>) => {
  if (active && payload && payload.length) {
    return (
      <Card css={{ p: "$3" }}>
        <Text>{label}</Text>
        <Flex>
          {payload.map((pld, index) => (
            <Flex key={`payload-item-${index}`}>
              <Text>{asCurrency(pld.value as number)}</Text>
            </Flex>
          ))}
        </Flex>
      </Card>
    );
  }
  return null;
};

export function StackedBarChart({
  chartData,
  filterKey,
  handleSelectStartDate,
  onSetActiveValue,
  onSelectPeriodDate,
}: {
  chartData: { name: string; sales: number; amt: number }[];
  filterKey: string;
  onSetActiveValue: ({ type, value }: { type: string; value: string }) => void;
  selectedMultiValues: {
    costCenter: string[];
    issuers: string[];
  };
  handleSelectStartDate: (startEndDate: MultiValue["startEndDate"]) => void;
  onSelectPeriodDate: (filterRangePeriod: MultiValue["filterRangePeriod"]) => void;
  onSelectMultiValues: ({
    value,
    field,
  }: {
    value: string;
    field: "costCenter" | "issuers" | "items" | "startEndDate";
  }) => void;
}) {
  function formatDateRange({ date, filterKey }: { date: Date; filterKey: string }) {
    switch (filterKey) {
      case "year":
        return {
          start: parseChartStartDate({ dateType: "month", date }),
          end: parseChartEndDate({ dateType: "month", date }),
        };
      case "day":
        return {
          start: parseChartStartDate({ dateType: "day", date }),
          end: parseChartEndDate({ dateType: "day", date }),
        };
      case "month":
        return {
          start: parseChartStartDate({ dateType: "day", date }),
          end: parseChartEndDate({ dateType: "day", date }),
        };
      case "threeMonths":
        return {
          start: parseChartStartDate({ dateType: "month", date }),
          end: parseChartEndDate({ dateType: "month", date }),
        };
      case "week":
        return {
          start: parseChartStartDate({ dateType: "day", date }),
          end: parseChartEndDate({ dateType: "day", date }),
        };
      default:
        return {
          start: date.toISOString(),
          end: date.toISOString(),
        };
    }
  }

  return (
    <ResponsiveContainer width="100%" height={350} style={{ marginTop: "20px" }}>
      <BarChart data={chartData} margin={{ top: 30, right: 10, left: 10, bottom: 40 }} barSize={40}>
        <CartesianGrid vertical={false} stroke="#E5E7EB" strokeDasharray="3 3" />
        <XAxis
          dataKey="name"
          tickCount={30}
          interval={0}
          tick={{ fill: "#6B7280", fontSize: 12 }}
          dy={10}
        />
        <YAxis axisLine={false} tickLine={false} tick={{ fill: "#6B7280", fontSize: 12 }} />

        <Bar
          dataKey="amt"
          name="Amount"
          radius={[4, 4, 0, 0]}
          barSize={50}
          cursor="pointer"
          onClick={({ date }) => {
            onSelectPeriodDate(null);
            onSelectPeriodDate({
              startDate: formatDateRange({
                date,
                filterKey,
              }).start,
              endDate: dayjs(
                formatDateRange({
                  date,
                  filterKey,
                }).end
              )
                .startOf("day")
                .toISOString(),
              isPeriodOnBar: true,
            });
          }}
          fill="#2563EB"
        >
          {chartData?.map((entry, index) => (
            <Cell key={`cell-${index}`} />
          ))}
          <LabelList
            dataKey="amt"
            position="top"
            fontSize={12}
            formatter={(value: number) => (value ? asCurrency(value) : null)}
          />
        </Bar>
        <Tooltip content={<CustomTooltip />} cursor={{ fill: "transparent", radius: "10" }} />
      </BarChart>
    </ResponsiveContainer>
  );
}
